// src/composables/useErrorLogger.ts
export interface ErrorInfo {
  type: 'vue-error' | 'vue-warning' | 'js-error' | 'unhandled-rejection'
  message: string
  stack?: string
  url: string
  timestamp: number
  component?: string
  props?: Record<string, any>
}

export interface ErrorLoggerConfig {
  reportError?: (errorInfo: ErrorInfo) => void | Promise<void>
  reportWarning?: boolean
  enableInDev?: boolean
}

// 🔥 重构：直接传入 app 实例，不依赖 getCurrentInstance()
export function setupErrorLogger(app: any, config: ErrorLoggerConfig) {
  const { reportError = defaultReportError, reportWarning = false, enableInDev = false } = config

  // 环境判断
  const isDev =
    typeof import.meta !== 'undefined'
      ? import.meta.env.DEV
      : process.env.NODE_ENV === 'development'

  // 开发环境未启用时，打印提示
  if (isDev && !enableInDev) {
    console.log('[ErrorLogger] 开发环境已禁用，如需测试请设置 enableInDev: true')
    return () => {}
  }

  // ------------------------------
  // 错误信息格式化
  // ------------------------------
  const formatErrorInfo = (
    type: ErrorInfo['type'],
    error: any,
    extra?: Partial<ErrorInfo>,
  ): ErrorInfo => {
    return {
      type,
      message: error?.message || String(error),
      stack: error?.stack,
      url: window.location.href,
      timestamp: Date.now(),
      ...extra,
    }
  }

  // ------------------------------
  // 1. Vue 错误处理
  // ------------------------------
  const originalErrorHandler = app.config.errorHandler
  app.config.errorHandler = (err: any, vm: any, info: string) => {
    const errorInfo = formatErrorInfo('vue-error', err, {
      component: vm?.$options?.name || 'AnonymousComponent',
      props: vm?.$props,
    })
    reportError(errorInfo)

    if (originalErrorHandler) {
      originalErrorHandler(err, vm, info)
    }
  }

  // ------------------------------
  // 2. Vue 警告处理（可选）
  // ------------------------------
  let originalWarnHandler: any
  if (reportWarning) {
    originalWarnHandler = app.config.warnHandler
    app.config.warnHandler = (msg: string, vm: any, trace: string) => {
      const errorInfo = formatErrorInfo('vue-warning', new Error(msg), {
        component: vm?.$options?.name || 'AnonymousComponent',
        stack: trace,
      })
      reportError(errorInfo)

      if (originalWarnHandler) {
        originalWarnHandler(msg, vm, trace)
      }
    }
  }

  // ------------------------------
  // 3. 浏览器 JS 同步错误
  // ------------------------------
  const handleJsError = (event: ErrorEvent) => {
    event.preventDefault()
    const errorInfo = formatErrorInfo('js-error', event.error || new Error(event.message))
    reportError(errorInfo)
  }

  // ------------------------------
  // 4. 未处理的 Promise 拒绝
  // ------------------------------
  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    event.preventDefault()
    const errorInfo = formatErrorInfo('unhandled-rejection', event.reason)
    reportError(errorInfo)
  }

  // ------------------------------
  // 注册全局事件监听
  // ------------------------------
  window.addEventListener('error', handleJsError, true)
  window.addEventListener('unhandledrejection', handleUnhandledRejection, true)

  // ------------------------------
  // 清理函数
  // ------------------------------
  const cleanup = () => {
    app.config.errorHandler = originalErrorHandler
    if (reportWarning) {
      app.config.warnHandler = originalWarnHandler
    }
    window.removeEventListener('error', handleJsError, true)
    window.removeEventListener('unhandledrejection', handleUnhandledRejection, true)
  }

  return cleanup
}

export function defaultReportError(errorInfo: ErrorInfo | Error | unknown, type = 'WEB') {
  if (!window.electronAPI) return

  let serialized: string

  if (typeof errorInfo === 'string') {
    serialized = errorInfo
  } else if (errorInfo instanceof Error) {
    // Error 对象属性不可枚举，需要手动提取
    const plainError: Record<string, unknown> = {
      name: errorInfo.name,
      message: errorInfo.message,
      stack: errorInfo.stack,
    }
    // 保留 cause 属性（ES2022+）
    if ((errorInfo as any).cause !== undefined) {
      plainError.cause = (errorInfo as any).cause
    }
    serialized = JSON.stringify(plainError)
  } else {
    // 其他对象（如 ErrorInfo）或基本类型
    try {
      serialized = JSON.stringify(errorInfo)
    } catch {
      // 处理循环引用等极端情况
      serialized = String(errorInfo)
    }
  }

  window.electronAPI?.sendLog(serialized, type)
}
